import React from 'react';
import { CompactEventRow } from './CompactEventRow';
import { FaceSmileIcon } from '@heroicons/react/24/outline';
import { ReactionPayload } from '../../../generated-api';
import { EMOJI_DISPLAY, EmojiType } from '../../reactions/types';

interface MergedReactionCardProps {
  reactions: Array<{ payload: ReactionPayload; createdAt: string }>;
}

/**
 * Helper to get emoji display character from emoji code.
 * Falls back to the code itself if not found.
 */
const getEmojiDisplay = (emoji: string): string => {
  const display = EMOJI_DISPLAY[emoji as EmojiType];
  return display ? display.emoji : emoji;
};

export const MergedReactionCard: React.FC<MergedReactionCardProps> = ({ reactions }) => {
  if (reactions.length === 0) return null;

  // Use the first reaction's playId and gameName (they should all be the same)
  const firstReaction = reactions[0];
  const playId = firstReaction.payload.playId;
  const gameName = firstReaction.payload.gameName;
  const latestCreatedAt = reactions[reactions.length - 1].createdAt;

  // Deduplicate reactions by playerId - keep only the latest reaction per user
  // (when a user changes their reaction, we only want to show their current one)
  const latestReactionByPlayer = new Map<string, { payload: ReactionPayload; createdAt: string }>();
  reactions.forEach((reaction) => {
    // Since reactions are in chronological order, later ones will overwrite earlier ones
    latestReactionByPlayer.set(reaction.payload.playerId, reaction);
  });
  const deduplicatedReactions = Array.from(latestReactionByPlayer.values());

  // Group deduplicated reactions by emoji
  const reactionsByEmoji: Record<string, Array<{ playerName: string; emoji: string }>> = {};
  deduplicatedReactions.forEach(({ payload }) => {
    const emoji = payload.emoji;
    if (!reactionsByEmoji[emoji]) {
      reactionsByEmoji[emoji] = [];
    }
    reactionsByEmoji[emoji].push({
      playerName: payload.playerName,
      emoji: emoji,
    });
  });

  // Get unique emojis for display
  const uniqueEmojis = Object.keys(reactionsByEmoji);
  const totalReactions = deduplicatedReactions.length;

  return (
    <CompactEventRow
      track="feed_reaction_card"
      to={`/plays/${playId}`}
      icon={<FaceSmileIcon className="h-4 w-4" />}
      iconClassName="bg-yellow-100 text-yellow-600"
      label={`${totalReactions} New Reaction${totalReactions > 1 ? 's' : ''}`}
      createdAt={latestCreatedAt}
    >
      {uniqueEmojis.map((emoji, index) => {
        const emojiReactions = reactionsByEmoji[emoji];
        const otherCount = emojiReactions.length - 1;
        return (
          <span key={emoji}>
            {index > 0 && ', '}
            <span className="font-medium text-heading">{emojiReactions[0].playerName}</span>
            {otherCount > 0 && ` +${otherCount}`}
            {' '}
            <span title={`${emojiReactions.length} ${emoji}`}>{getEmojiDisplay(emoji)}</span>
          </span>
        );
      })}
      {' on '}
      <span className="font-medium">{gameName}</span>
    </CompactEventRow>
  );
};
